import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import SEO from '../components/SEO'
import ProductCard from '../components/ProductCard'
import { fetchCollectionV2 } from '../lib/cms'

const DEFAULT_CATEGORIES = [
  {
    slug: 'kabiner',
    navn: 'Kabiner',
    beskrivelse: 'Blæse- og matteringskabiner i kraftig kvalitet til sandblæsning, glasblæsning og lakforberedelse (PART-BLASTER, SSB & LSB modeller).',
  },
  {
    slug: 'vandpoleringsmaskiner',
    navn: 'Vandpoleringsmaskiner',
    beskrivelse: 'Danskproducerede KWB vandpoleringsmaskiner i fuldsvejst rustfrit stål til støvfri rengøring og polering.',
  },
  {
    slug: 'mobile-blaesere',
    navn: 'Mobile Blæsere',
    beskrivelse: 'Mobile Ibix blæsesystemer og udstyr til præcisionsblæsning på stedet.',
  },
  {
    slug: 'kompressorer',
    navn: 'Kompressorer',
    beskrivelse: 'Stempel- og skruekompressorer fra Reno tilpasset autoværksteder og industribrug.',
  },
  {
    slug: 'toerisblaesning',
    navn: 'Tørisblæsning',
    beskrivelse: 'Kärcher Ice Blasters med L2P Liquid-to-Pellet teknologi. Skånsom, kemi-fri og vandfri afrensning.',
  },
  {
    slug: 'forbrugsmaterialer',
    navn: 'Forbrugsmaterialer & Blæsemedier',
    beskrivelse: 'Specialudviklede blæsemedier, korrosionsbeskyttelse, rensevæsker og imprægneringsmidler afstemt til kabinestørrelser.',
  },
  {
    slug: 'reservedele-tilbehoer',
    navn: 'Reservedele & Tilbehør',
    beskrivelse: 'Reservedele, handsker, viskerkit, ruder, riste, spulehoveder, filtre og ekstraudstyr i højeste kvalitet.',
  },
]

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentCategory = searchParams.get('kategori') || 'alle'
  const [searchQuery, setSearchQuery] = useState('')

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES)
  const [products, setProducts] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)

  // Load categories from CMS if available, otherwise fallback/extend
  useEffect(() => {
    async function loadCategories() {
      try {
        const catData = await fetchCollectionV2('produktkategorier', {
          sort: 'sortering',
          order: 'asc',
          limit: 20,
        })
        if (catData.items && catData.items.length > 0) {
          // Merge or keep defined 4 split categories
          setCategories(DEFAULT_CATEGORIES)
        }
      } catch (err) {
        console.error('Failed to load categories:', err)
      }
    }
    loadCategories()
  }, [])

  // Load products from CMS
  useEffect(() => {
    async function loadProducts() {
      setLoading(true)
      try {
        // Fetch all products so we can accurately filter across the 4 newly split categories
        const data = await fetchCollectionV2('produkter', {
          limit: 100,
          sort: 'sortering',
          order: 'asc',
        })
        setProducts(data.items || [])
        setTotalCount(data.total || (data.items ? data.items.length : 0))
      } catch (err) {
        console.error('Failed to load products:', err)
      } finally {
        setLoading(false)
      }
    }
    loadProducts()
  }, [])

  function handleCategoryChange(slug) {
    if (slug === 'alle') {
      searchParams.delete('kategori')
      setSearchParams(searchParams)
    } else {
      setSearchParams({ kategori: slug })
    }
  }

  // Filter products by category and search
  const filteredProducts = products.filter((p) => {
    // Category check
    if (currentCategory && currentCategory !== 'alle') {
      const pCat = p.produktkategori || ''
      const pName = (p.produktnavn || '').toLowerCase()

      if (currentCategory === 'kabiner') {
        // Blæsekabiner (LSB / SSB Aero Blaster or explicit kabiner without wet/vandpoler)
        const isVandpoler = pName.includes('wet') || pName.includes('vandpoler') || pName.includes('kwb-')
        if (pCat === 'kabiner' && isVandpoler) return false
        if (pCat !== 'kabiner' && pCat !== 'blaesekabiner') return false
      } else if (currentCategory === 'vandpoleringsmaskiner') {
        // Vandpoleringsmaskiner (KWB models)
        const isVandpoler = pName.includes('wet') || pName.includes('vandpoler') || pName.includes('kwb-')
        if (pCat === 'vandpoleringsmaskiner') return true
        if (pCat === 'kabiner' && isVandpoler) return true
        return false
      } else if (currentCategory === 'mobile-blaesere') {
        // Mobile blæsere (Ibix modeller)
        const isMobile = pName.includes('ibix') || pName.includes('mobil')
        if (pCat === 'mobile-blaesere') return true
        if (pCat === 'kompressorer-udstyr' && isMobile) return true
        return false
      } else if (currentCategory === 'kompressorer') {
        // Kompressorer (Reno etc.)
        const isKompressor = pName.includes('kompressor') || pName.includes('reno')
        if (pCat === 'kompressorer') return true
        if (pCat === 'kompressorer-udstyr' && isKompressor) return true
        return false
      } else if (pCat !== currentCategory) {
        return false
      }
    }

    // Search query check
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    const name = (p.produktnavn || '').toLowerCase()
    const desc = (p.produktbeskrivelse || '').toLowerCase()
    return name.includes(q) || desc.includes(q)
  })

  // Selected category info
  const selectedCatObj = categories.find((c) => (c.slug?._ || c.slug) === currentCategory)

  return (
    <>
      <SEO
        title={
          selectedCatObj
            ? `${selectedCatObj.navn} – Kryger Wet Blaster`
            : 'Produkter – Kryger Wet Blaster'
        }
        description="Komplet oversigt over Krüger Wet Blaster produkter: Vandpoleringsmaskiner, blæsekabiner, tørisblæsere, reservedele og forbrugsstoffer."
      />

      {/* Header Banner */}
      <section className="bg-slate-900 text-white border-b border-slate-800 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">
              Produkter
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              Vi tilbyder komplette løsninger, alle former for våd og tør blæsning og afrensning – ikke kun maskiner, men også reservedele og forbrugsstoffer til en effektiv og støvfri hverdag.
            </p>
            <p className="text-slate-400 text-sm mt-2">
              Hvis du ikke kan finde det du søger er du mere end velkommen til at kontakte, så skal vi nok finde en løsning sammen.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {/* Category Navigation Bar */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-200">
            {/* Category pills */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleCategoryChange('alle')}
                className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                  currentCategory === 'alle'
                    ? 'bg-sky-600 text-white shadow-sm'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                Alle produkter
              </button>

              {categories.map((cat) => {
                const catSlug = cat.slug?._ || cat.slug
                const isActive = currentCategory === catSlug

                return (
                  <button
                    key={cat.id || catSlug}
                    type="button"
                    onClick={() => handleCategoryChange(catSlug)}
                    className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                      isActive
                        ? 'bg-sky-600 text-white shadow-sm'
                        : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    {cat.navn}
                  </button>
                )
              })}
            </div>

            {/* Quick Search */}
            <div className="relative w-full sm:w-72">
              <i className="fa-solid fa-magnifying-glass absolute left-0 top-1/2 -translate-y-1/2 text-slate-400 text-xs" aria-hidden="true" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Søg blandt produkter..."
                className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none pl-6 pr-2 py-2 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-600 focus:ring-0"
              />
            </div>
          </div>

          {/* Category Description Banner if one is selected */}
          {selectedCatObj && (
            <div className="pt-2 pb-1 flex flex-col sm:flex-row sm:items-baseline justify-between gap-3 border-b border-slate-200">
              <div>
                <h2 className="text-base font-bold text-slate-900">
                  {selectedCatObj.navn}
                </h2>
                {selectedCatObj.beskrivelse && (
                  <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                    {selectedCatObj.beskrivelse}
                  </p>
                )}
              </div>
              <div className="text-xs font-semibold text-slate-500 whitespace-nowrap">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'produkt' : 'produkter'} i kategorien
              </div>
            </div>
          )}
        </div>

        {/* Product Grid or Empty State */}
        {loading ? (
          <div className="py-24 text-center text-slate-400">
            <i className="fa-solid fa-spinner fa-spin text-3xl mb-3 text-sky-500" />
            <p className="text-sm">Henter produkter fra katalog...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="py-20 text-center bg-white rounded-xl border border-slate-200 p-8 space-y-4">
            <div className="text-slate-400 text-4xl mx-auto">
              <i className="fa-solid fa-box-open" aria-hidden="true" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Ingen produkter fundet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              {searchQuery
                ? `Ingen produkter matchede din søgning på "${searchQuery}". Prøv et andet søgeord.`
                : 'Der er i øjeblikket ingen produkter i denne kategori.'}
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                handleCategoryChange('alle')
              }}
              className="inline-flex items-center gap-2 bg-sky-600 text-white text-xs font-semibold px-4 py-2 rounded hover:bg-sky-500 transition-colors"
            >
              Nulstil filtre
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id || (product.slug?._ || product.slug)} product={product} />
            ))}
          </div>
        )}

        {/* Need Help Banner */}
        <div className="mt-16 bg-slate-900 text-white rounded-none p-8 sm:p-10 border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl font-bold">Har du særlige ønsker eller spørgsmål til en maskine?</h3>
            <p className="text-sm text-slate-300 max-w-xl">
              Vi rådgiver dig gerne om korrekt blæsemedie, dysestørrelser, vandtryk og specialtilpassede kabiner.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="tel:+4521438149"
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm px-5 py-3 rounded-none shadow transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring 21 43 81 49</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 border border-white bg-transparent hover:bg-transparent text-white font-medium text-sm px-5 py-3 rounded-none transition-colors"
            >
              <span>Skriv til os</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
